import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl';
import { Link } from './Link';

const messages = defineMessages({
  tools: {
    id: 'tools.title',
    defaultMessage: 'Tools',
    description: 'Tools section title in tool switcher navigation',
  },
  jobFlow: {
    id: 'tools.jobFlow',
    defaultMessage: 'Job Flow',
    description: 'Job flow visualization tool link in tool switcher',
  },
  viewedJobs: {
    id: 'tools.viewedJobs',
    defaultMessage: 'Viewed Jobs',
    description: 'Viewed jobs page link in tool switcher',
  },
  savedJobs: {
    id: 'tools.savedJobs',
    defaultMessage: 'Saved Jobs',
    description: 'Saved jobs page link in tool switcher',
  },
  appliedJobs: {
    id: 'tools.appliedJobs',
    defaultMessage: 'Applied Jobs',
    description: 'Applied jobs page link in tool switcher',
  },
  interviewingJobs: {
    id: 'tools.interviewingJobs',
    defaultMessage: 'Interviewing Jobs',
    description: 'Interviewing jobs page link in tool switcher',
  },
  offerJobs: {
    id: 'tools.offerJobs',
    defaultMessage: 'Offer Jobs',
    description: 'Offer jobs page link in tool switcher',
  },
  rejectedJobs: {
    id: 'tools.rejectedJobs',
    defaultMessage: 'Rejected Jobs',
    description: 'Rejected jobs page link in tool switcher',
  },
  hiredJobs: {
    id: 'tools.hiredJobs',
    defaultMessage: 'Hired Jobs',
    description: 'Hired jobs page link in tool switcher',
  },
  hiddenJobs: {
    id: 'tools.hiddenJobs',
    defaultMessage: 'Hidden Jobs',
    description: 'Hidden jobs page link in tool switcher',
  },
  recentSearches: {
    id: 'tools.recentSearches',
    defaultMessage: 'Saved Searches',
    description: 'Recent searches page link in tool switcher',
  },
});

interface ToolSwitcherProps {
  currentTool: string;
  variant?: 'default' | 'navbar';
}

const ToolSwitcher: React.FC<ToolSwitcherProps> = ({ currentTool, variant = 'default' }) => {
  const intl = useIntl();

  // Normalize path by removing trailing slash (except for root)
  const normalizePath = (path: string) => {
    if (path === '/') return path;
    return path.replace(/\/$/, '');
  };
  const normalizedCurrentTool = normalizePath(currentTool);

  // Debug: log normalized current tool
  console.log('ToolSwitcher: normalizedCurrentTool =', normalizedCurrentTool);

  const tools = [
    {
      route: '/tools/job-flow-sankey',
      label: intl.formatMessage(messages.jobFlow),
    },
    {
      route: '/tools/viewed-jobs',
      label: intl.formatMessage(messages.viewedJobs),
    },
    { route: '/tools/saved-jobs', label: intl.formatMessage(messages.savedJobs) },
    {
      route: '/tools/applied-jobs',
      label: intl.formatMessage(messages.appliedJobs),
    },
    {
      route: '/tools/interviewing-jobs',
      label: intl.formatMessage(messages.interviewingJobs),
    },
    {
      route: '/tools/offer-jobs',
      label: intl.formatMessage(messages.offerJobs),
    },
    {
      route: '/tools/rejected-jobs',
      label: intl.formatMessage(messages.rejectedJobs),
    },
    {
      route: '/tools/hired-jobs',
      label: intl.formatMessage(messages.hiredJobs),
    },
    {
      route: '/tools/hidden-jobs',
      label: intl.formatMessage(messages.hiddenJobs),
    },
    {
      route: '/tools/saved-searches',
      label: intl.formatMessage(messages.recentSearches),
    },
  ];

  // Compact navbar variant - renders inline in navbar
  if (variant === 'navbar') {
    return (
      <div className="flex items-center gap-2 overflow-x-auto flex-1 px-2 scrollbar-hide">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
          {intl.formatMessage(messages.tools)}:
        </span>
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
          {tools.map((tool) => {
            const normalizedRoute = normalizePath(tool.route);
            return (
              <Link
                key={tool.route}
                href={tool.route}
                className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  normalizedCurrentTool === normalizedRoute
                    ? 'bg-green-100 text-green-900 border border-green-300 dark:bg-green-200 dark:text-black dark:border-green-300'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
                }`}
              >
                {tool.label}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // Default variant - full page header
  return (
    <>
      {/* Desktop/tablet version */}
      <div className="bg-white/95 dark:bg-gray-900/95 shadow-md border-x border-b border-gray-200 dark:border-gray-700 rounded-b-2xl font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif] md:mt-4 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-6 pb-1">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 text-center">{intl.formatMessage(messages.tools)}</h2>
            <div className="flex space-x-4 overflow-x-auto justify-start sm:justify-center px-4 scroll-px-4">
              {tools.map((tool) => {
                const normalizedRoute = normalizePath(tool.route);
                console.log('ToolSwitcher: comparing', normalizedCurrentTool, 'with', normalizedRoute);
                return (
                  <Link
                    key={tool.route}
                    href={tool.route}
                    className={`px-4 py-2 rounded-md text-sm font-medium border border-gray-200 dark:border-gray-600 ${
                      normalizedCurrentTool === normalizedRoute
                        ? 'bg-green-100 text-green-900 border-green-300 dark:bg-green-200 dark:text-black dark:border-green-300'
                        : 'text-gray-500 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {tool.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile sticky bottom navbar - iOS 18 style */}
      <nav
        className="fixed bottom-0 left-0 w-full z-50 flex justify-between items-center px-1 py-1 border-t border-black/10 dark:border-gray-700 md:hidden bg-white/95 dark:bg-gray-900/95 dark:text-gray-100 shadow rounded-t-3xl transition-colors duration-300 overflow-hidden"
        style={{
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
          WebkitBackdropFilter: 'blur(24px)',
          backdropFilter: 'blur(24px)',
        }}
      >
        {tools.map((tool, idx) => {
          const normalizedRoute = normalizePath(tool.route);
          console.log('ToolSwitcher (mobile): comparing', normalizedCurrentTool, 'with', normalizedRoute);
          return (
            <React.Fragment key={tool.route}>
              <Link
                href={tool.route}
                className={`flex-1 flex flex-col items-center min-w-0 py-2 px-0 rounded-xl text-xs font-semibold border border-transparent transition-colors duration-200 mx-0.5 ${
                  normalizedCurrentTool === normalizedRoute
                    ? 'bg-green-100/80 dark:bg-green-200/80 text-green-900 dark:text-black border-green-300 dark:border-green-300 shadow-md'
                    : 'text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-100 hover:bg-gray-100/60 dark:hover:bg-gray-800/60'
                }`}
                style={{ minWidth: 0 }}
              >
                <span
                  className="truncate whitespace-normal break-words text-center leading-tight font-semibold text-xs min-h-[2.25rem] leading-[1.1]"
                >
                  {tool.label}
                </span>
              </Link>
              {idx < tools.length - 1 && <div className="w-px h-7 bg-gray-200 dark:bg-gray-700 mx-0.5" />}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
};

export default ToolSwitcher;
